import React, { useEffect, useState } from "react";
import { baseUrl } from '../../config.js';
import useToken from '../../useToken';
import { Archivo } from "../InfoAlmacen/InfoAlmacen.js";
import './Perfil.css';

import defaultPersona from '../../img/nombre.png';

const Perfil = ({ perfil, setPerfiles, setModal, archivos, setArchivos }) => {
    const { token } = useToken();
    const [nombre, setNombre] = useState(perfil?.name || "");
    const [rfc, setRfc] = useState(perfil?.rfc || "");
    const [guardando, setGuardando] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (perfil?.documents?.length > 0) {
            setArchivos(archivos.map(archivo => {
                const documento = perfil.documents.find(item => item.idDocument === archivo.idDocument);
                return documento ? { ...archivo, ...documento } : archivo;
            }));
        }
        // Los documentos base se cargan desde el componente padre.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [perfil]);

    const rfcValido = (valor) => {
        if (!valor) return false;
        return /^([A-ZÑ&]{3}|[A-ZÑ&]{4})[0-9]{6}[A-Z0-9]{3}$/.test(valor.trim().toUpperCase());
    };

    const guardarArchivos = async (idProfile) => {
        const pendientes = archivos.filter(archivo => archivo.file);

        await Promise.all(pendientes.map(async archivo => {
            const data = new FormData();
            data.append('idProfile', idProfile);
            data.append('idDocument', archivo.idDocument);
            data.append('file', archivo.file);

            const response = await fetch(baseUrl + "Document/Profile", {
                method: "post",
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: data,
            });
            const json = await response.json();
            if (!response.ok || !json.success) {
                throw new Error(json.error || `No se pudo subir ${archivo.name}.`);
            }
        }));

        if (pendientes.length) {
            setArchivos(archivos.map(archivo => (
                archivo.file ? { ...archivo, file: undefined, idStatusDocument: 6 } : archivo
            )));
        }
    };

    const subirArchivoInmediato = async (archivoEditado) => {
        if (!perfil?.idProfile) return; // Si es un perfil nuevo, se subirán al darle click en Guardar.
        
        const data = new FormData();
        data.append('idProfile', perfil.idProfile);
        data.append('idDocument', archivoEditado.idDocument);
        data.append('file', archivoEditado.file);

        try {
            const response = await fetch(baseUrl + "Document/Profile", {
                method: "post",
                headers: {
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: data,
            });
            const json = await response.json();
            if (response.ok && json.success) {
                setArchivos(prev => prev.map(a => a.idDocument === archivoEditado.idDocument ? { ...a, file: undefined, idStatusDocument: 6 } : a));
            }
        } catch (error) {
            console.error("Error al subir archivo a S3:", error);
        }
    };

    const guardar = async (event) => {
        event.preventDefault();
        if (guardando) return;

        const nombreNormalizado = nombre.trim();
        const rfcNormalizado = rfc.trim().toUpperCase();

        if (!nombreNormalizado) {
            setError("Ingresa el nombre o razón social.");
            return;
        }
        if (!rfcValido(rfcNormalizado)) {
            setError("Ingresa un RFC válido de 12 o 13 caracteres.");
            return;
        }

        setGuardando(true);
        setError("");

        try {
            const response = await fetch(baseUrl + "Profile", {
                method: perfil?.idProfile ? "put" : "post",
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify({
                    ...perfil,
                    name: nombreNormalizado,
                    rfc: rfcNormalizado,
                }),
            });
            const json = await response.json();
            if (!response.ok || !json.success) {
                throw new Error(json.error || "No se pudo guardar el perfil fiscal.");
            }

            const perfilGuardado = {
                ...perfil,
                idProfile: json.idProfile,
                name: nombreNormalizado,
                rfc: rfcNormalizado,
            };

            await guardarArchivos(json.idProfile);
            setPerfiles(perfiles => {
                const existe = perfiles.some(item => item.idProfile === json.idProfile);
                return existe
                    ? perfiles.map(item => item.idProfile === json.idProfile ? perfilGuardado : item)
                    : [...perfiles, perfilGuardado];
            });
            setModal(false);
        } catch (err) {
            console.error(err);
            setError(err.message || "Ocurrió un error al guardar el perfil.");
        } finally {
            setGuardando(false);
        }
    };

    const datosCompletos = nombre.trim() && rfcValido(rfc);

    return (
        <form className="perfil-editor" onSubmit={guardar}>
            <header className="perfil-editor__intro">
                <div className="perfil-editor__avatar">
                    <img src={perfil?.url || defaultPersona} alt="Perfil fiscal" />
                </div>
                <div>
                    <span className="perfil-editor__eyebrow">Perfil de facturación</span>
                    <h2>{perfil?.idProfile ? "Actualiza tus datos fiscales" : "Agrega tus datos fiscales"}</h2>
                    <p>Usaremos esta información para identificar al titular y emitir los documentos de tu reservación.</p>
                </div>
            </header>

            <section className="perfil-editor__section">
                <div className="perfil-editor__section-head">
                    <span className="perfil-editor__step">1</span>
                    <div>
                        <h3>Información fiscal</h3>
                        <p>Captura los datos exactamente como aparecen en tu constancia fiscal.</p>
                    </div>
                </div>
                <div className="perfil-editor__fields">
                    <label>
                        <span>Nombre o razón social</span>
                        <input
                            type="text"
                            value={nombre}
                            placeholder="Ej. Luis Donaldo"
                            autoComplete="name"
                            required
                            onChange={(event) => setNombre(event.target.value)}
                        />
                    </label>
                    <label>
                        <span>RFC</span>
                        <input
                            type="text"
                            value={rfc}
                            placeholder="Ej. CAPL941115EJ9"
                            maxLength="13"
                            autoCapitalize="characters"
                            required
                            onChange={(event) => setRfc(event.target.value.toUpperCase())}
                        />
                        <small>12 caracteres para persona moral o 13 para persona física.</small>
                    </label>
                </div>
            </section>

            <section className="perfil-editor__section">
                <div className="perfil-editor__section-head">
                    <span className="perfil-editor__step">2</span>
                    <div>
                        <h3>Documentos de respaldo</h3>
                        <p>Adjunta los archivos solicitados en formato PDF, con un máximo de 25 MB.</p>
                    </div>
                </div>
                {datosCompletos ? (
                    <div className="perfil-editor__docs">
                        {archivos?.map(archivo => (
                            <Archivo
                                key={archivo.idDocument}
                                archivo={archivo}
                                archivos={archivos}
                                setArchivos={setArchivos}
                                guardar={() => subirArchivoInmediato(archivo)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="perfil-editor__notice">
                        <span>!</span>
                        <div>
                            <strong>Completa primero la información fiscal</strong>
                            <p>Los documentos se habilitarán cuando el nombre y el RFC sean válidos.</p>
                        </div>
                    </div>
                )}
            </section>

            {error && <div className="perfil-editor__error" role="alert">{error}</div>}

            <footer className="perfil-editor__actions">
                <button
                    type="button"
                    className="perfil-editor__cancel"
                    onClick={() => setModal(false)}
                    disabled={guardando}
                >
                    Cancelar
                </button>
                <button
                    type="submit"
                    className="perfil-editor__save"
                    disabled={guardando || !datosCompletos}
                >
                    {guardando ? "Guardando..." : perfil?.idProfile ? "Guardar cambios" : "Registrar perfil"}
                </button>
            </footer>
        </form>
    );
};

export default Perfil;
